import { T } from "src/apps/components/i18n/Translation";

import styled from "styled-components";
import { Text } from "src/apps/components/typography/Texts";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { ChangeEvent, DragEvent, useRef } from "react";
import { useUploader } from "src/apps/hooks/uploadFiles/useUploader";
import { OrderItem } from "src/contexts/documentationArea/domain/OrderItem";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";
import { IDVMobileIdleOrderBoxUploader } from "./IDVMobileIdleOrderBoxUploader";
import { Button } from "src/apps/components/button/Button";
import { LuArrowUpFromLine } from "react-icons/lu";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { colors } from "@permisso/permisso-components";
import { ACCEPTED_UPLOAD_FILE_TYPES } from "src/apps/providers/uploaders/constants";
import { TEST_IDS } from "src/apps/config/testIds";

const ACCEPTED_UPLOADS = "documentation_area.uploader_box.accepted_uploads";

interface IDVIdleStateUploaderProps {
  order: OrderItem;
  side: IdentityVerificationDocumentSides;
  isDragOver?: boolean;
  onDragEnd?: () => void;
}

export const IDVIdleOrderBoxUploader = ({
  order,
  side,
  isDragOver,
  onDragEnd,
}: IDVIdleStateUploaderProps) => {
  const { isMobile } = useIsMedia();
  const { t } = useTranslations();
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const { convertAndUploadIdentityVerificationFiles } = useUploader();

  const convertAndUpload = (
    orderId: string,
    side: IdentityVerificationDocumentSides,
    fileList: FileList | null,
  ) => {
    const files = Array.from(fileList ?? []);
    convertAndUploadIdentityVerificationFiles(orderId, side, files);
  };

  const onFileDrop = async (e: ChangeEvent<HTMLInputElement>) => {
    convertAndUpload(order.getId(), side, e.target.files);
    e.target.value = "";
    return;
  };

  const onUploadClick = () => {
    hiddenInputRef.current?.click();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    onDragEnd?.();
    convertAndUpload(order.getId(), side, event.dataTransfer.files);
  };

  if (isMobile) {
    return <IDVMobileIdleOrderBoxUploader side={side} onFileDrop={onFileDrop} />;
  }

  return (
    <DesktopUploader onDrop={handleDrop}>
      <UploadInput
        ref={hiddenInputRef}
        id="files"
        data-testid={TEST_IDS.identityVerification.fileInput(side)}
        onChange={onFileDrop}
        accept={ACCEPTED_UPLOAD_FILE_TYPES}
        multiple={false}
      />
      {!isDragOver && (
        <NonHoveredContainer>
          <Button
            variant="outline"
            text={t("common.upload_file")}
            onClick={onUploadClick}
            leftIcon={<LuArrowUpFromLine size={20} />}
          />
          <CenteredContainer>
            <TextSmall $color={colors.grey500}>
              <T k={ACCEPTED_UPLOADS} />
            </TextSmall>
          </CenteredContainer>
        </NonHoveredContainer>
      )}
    </DesktopUploader>
  );
};

const NonHoveredContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;
`;

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const UploadInput = styled.input.attrs({ type: "file" })`
  display: none;
`;

const DesktopUploader = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  border-radius: 6px;
  background-color: ${colors.grey0};
`;

const TextSmall = styled(Text)`
  font-size: 0.75rem;
  line-height: 1rem;
`;
